import { useState } from "react";
import { toast } from "sonner";
import { Check, Link2 } from "lucide-react";
import { T, useContent, useText } from "@/context/ContentContext";

const WhatsAppGlyph = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3zM12 21.8c-1.8 0-3.5-.5-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4C2.7 15.6 2.2 13.8 2.2 12 2.2 6.6 6.6 2.2 12 2.2c2.6 0 5.1 1 6.9 2.9 1.8 1.8 2.9 4.3 2.9 6.9 0 5.4-4.4 9.8-9.8 9.8zM20.5 3.5C18.2 1.2 15.2 0 12 0 5.4 0 0 5.4 0 12c0 2.1.6 4.2 1.6 6L0 24l6.2-1.6c1.8 1 3.8 1.5 5.8 1.5 6.6 0 12-5.4 12-12 0-3.2-1.2-6.2-3.5-8.4z" />
  </svg>
);

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = Object.assign(document.createElement("textarea"), { value: text });
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

/** WhatsApp + copy-link buttons for a page. The share message is editable in the visual editor. */
export function ShareButtons({ k, name, path, className = "" }) {
  const [copied, setCopied] = useState(false);
  const { editing } = useContent();
  const fallback = `Look at this ${name} trip from Trippy Tastic!`;
  const message = useText(`${k}.share.text`, fallback);
  const url = `${window.location.origin}${path}`;
  const btn = "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors";

  const copy = async () => {
    if (await copyText(url)) {
      setCopied(true);
      toast.success("Link copied. Paste it anywhere to share.");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Couldn't copy the link. Please copy it from the address bar.");
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="share-buttons">
      <span className="eyebrow text-cream/75 mr-1">Share</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${message} ${url}`)}`} target="_blank" rel="noreferrer"
        className={`${btn} bg-[#25D366] text-ink hover:bg-[#1ebe5b]`} data-testid="share-whatsapp-btn">
        <WhatsAppGlyph /> WhatsApp
      </a>
      <button type="button" onClick={copy} className={`${btn} border border-cream/30 text-cream hover:bg-cream hover:text-ink`} data-testid="share-copy-link-btn">
        {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? "Copied" : "Copy link"}
      </button>
      {editing && (
        <p className="basis-full text-xs text-cream/80" data-testid="share-message-editor">
          WhatsApp message: <T k={`${k}.share.text`}>{fallback}</T>
        </p>
      )}
    </div>
  );
}
